import {
    FGStack,
    FGButton,
    FGIcons,
    FGCounter,
    COUNTER_VARIANTS,
} from '@fg/design-system';
import React, { useMemo } from 'react';
import ImpactFilter from './ImpactFilter';
import FeedSeearch from './FeedSearch';
import FeedDateFilter from './FeedDateFilter';
import FeedCountryFilter from './FeedCountryFilter';
import { isEmpty, toString } from 'lodash/fp';

type Props = {
    isShowCountryFilter?: boolean;
    isShowDateFilter?: boolean;
    isShowImpactFilter?: boolean;
    impactFilter?: number[];
    maxDate: moment.Moment;
    minDate?: moment.Moment;
    selectedCountries: number[];
    searchString: string;
    onSetSearchString: (value: string) => void;
    removeCountry: (countryId: number) => void;
    addCountry: (countryId: number) => void;
    toggleCountry: (countryId: number) => void;
    onDateRangeChange: (startDate: Date, endDate: Date) => void;
    onDateRangeReset: () => void;
};

export const FeedHeader = React.memo(
    ({
        isShowCountryFilter,
        isShowDateFilter,
        isShowImpactFilter,
        impactFilter,
        maxDate,
        minDate,
        selectedCountries,
        searchString,
        onSetSearchString,
        removeCountry,
        addCountry,
        toggleCountry,
        onDateRangeChange,
        onDateRangeReset,
    }: Props) => {
        const [isShowFilters, setIsShowFilters] = React.useState(false);
        const [isShowSearch, setIsShowSearch] = React.useState(false);
        const numFilters = useMemo(() => {
            let sum = 0;
            if (isShowImpactFilter && !isEmpty(impactFilter)) sum++;
            if (isShowDateFilter && maxDate) sum++;
            if (isShowCountryFilter && !isEmpty(selectedCountries)) sum++;
            return sum;
        }, [
            impactFilter,
            maxDate,
            selectedCountries,
            isShowImpactFilter,
            isShowDateFilter,
            isShowCountryFilter,
        ]);
        const activeBgColor = 'var(--fg-color-surface-secondary)';
        return (
            <FGStack direction="column" className="events-feed-header" data-testid="feed-toolbar">
                <FGStack
                    gap={8}
                    style={{
                        position:
                            'absolute' /*todo: remove absolute+inset when SideBar allows rendering a custom title*/,
                        top: 0,
                        right: 0,
                    }}>
                    <FGButton
                        data-testid="feed-search-toggle"
                        variant="secondary"
                        style={{
                            backgroundColor: isShowSearch
                                ? activeBgColor
                                : undefined,
                        }}
                        aria-label="Search events"
                        title="Search events"
                        icon={<FGIcons icon="Search" />}
                        onClick={() => setIsShowSearch(!isShowSearch)}
                    />
                    <FGButton
                        data-testid="feed-filters-toggle"
                        style={{
                            backgroundColor: isShowFilters
                                ? activeBgColor
                                : undefined,
                            position: 'relative',
                        }}
                        variant="secondary"
                        aria-label="Event Filters"
                        title="Event Filters"
                        icon={<FGIcons icon="Filter" />}
                        onClick={() => setIsShowFilters(!isShowFilters)}>
                        {!isShowFilters && numFilters > 0 && (
                                <FGCounter
                                    variant={COUNTER_VARIANTS.important}
                                    label={toString(numFilters)}
                                    className="events-feed-header-counter"
                                />
                            )}
                    </FGButton>
                </FGStack>
                {isShowSearch && (
                    <FGStack direction="column">
                        <FeedSeearch
                            searchString={searchString}
                            onSetSearchString={onSetSearchString}
                        />
                    </FGStack>
                )}
                {isShowFilters && (
                    <FGStack
                        direction="column"
                        gap={8}
                        className="events-feed-header-filters-list">
                        {isShowImpactFilter && <ImpactFilter />}
                        {isShowDateFilter && (
                            <FeedDateFilter
                                maxDate={maxDate}
                                minDate={minDate}
                                onChange={onDateRangeChange}
                                onClear={onDateRangeReset}
                            />
                        )}
                        {isShowCountryFilter && (
                            <FeedCountryFilter
                                selectedCountries={selectedCountries}
                                removeCountry={removeCountry}
                                addCountry={addCountry}
                                toggleCountry={toggleCountry}
                            />
                        )}
                    </FGStack>
                )}
            </FGStack>
        );
    }
);

export default FeedHeader;
